import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const;
const COUNTDOWN_TICK_MS = 250;
// A mousemove fires ~60 times a second, and each one would otherwise restart every tab's timers.
// Idle periods are minutes long, so counting at most one interaction per second loses nothing.
const ACTIVITY_THROTTLE_MS = 1000;

type IdleTimerMessage = { type: 'activity' } | { type: 'stay-signed-in' };

export interface UseIdleTimerOptions {
  /** Milliseconds of no interaction before onIdle fires. */
  idleTimeoutMs: number;
  /** Called when the idle period elapses without cancellation. */
  onIdle: () => void;
  /** Milliseconds before idleTimeoutMs to enter the warning state. Omit/0 = no warning phase. */
  warningLeadMs?: number;
  /** Whether the timer is active at all. false = no listeners, no timers. Default true. */
  enabled?: boolean;
  /** BroadcastChannel name for cross-tab activity sync (FR-006). Omit to disable. */
  channelName?: string;
}

export interface UseIdleTimerResult {
  /** True while inside the warning lead time, before onIdle fires. */
  isWarning: boolean;
  /** Milliseconds remaining until onIdle fires, while isWarning is true. */
  remainingMs: number;
  /** Cancel the pending warning/idle and restart the full idle period, in every tab. */
  reset: () => void;
}

/**
 * Fires onIdle after idleTimeoutMs with no mouse, keyboard, scroll, or touch interaction. Any
 * other event (e.g. a background data refresh) is not treated as activity. When warningLeadMs is
 * set, isWarning/remainingMs surface a cancelable countdown before onIdle fires. When
 * channelName is set, local interaction is broadcast so every tab sharing that channel resets
 * together (research.md R2). reset() is broadcast too, and cancels other tabs' warnings — tabs
 * reach the warning together, so a tab left counting down would sign out the one just kept alive.
 */
export function useIdleTimer({
  idleTimeoutMs,
  onIdle,
  warningLeadMs = 0,
  enabled = true,
  channelName,
}: UseIdleTimerOptions): UseIdleTimerResult {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const [isWarning, setIsWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(warningLeadMs);
  // Once the warning is showing, this tab's own ambient activity (mouse movement while reaching for
  // the button) must not cancel it — only reset() or another tab's activity may. Another tab only
  // broadcasts activity while its own warning is not showing, so that activity is real presence,
  // and ignoring it would let this tab sign out a tab in use. Set directly at each setIsWarning
  // call site below, not synced from the isWarning state var — React batches the re-render that
  // would update it until the end of the current event/act(), so a same-tick activity event would
  // otherwise still read the stale value.
  const isWarningRef = useRef(false);

  // window.setTimeout/clearTimeout, not the bare global — this workspace also has @types/node
  // in scope, whose NodeJS.Timeout-returning overloads would otherwise conflict with DOM's.
  const idleTimerRef = useRef<number | undefined>(undefined);
  const warningTimerRef = useRef<number | undefined>(undefined);
  const countdownRef = useRef<number | undefined>(undefined);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastActivityAtRef = useRef(Number.NEGATIVE_INFINITY);

  const clearAllTimers = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    window.clearTimeout(warningTimerRef.current);
    window.clearInterval(countdownRef.current);
  }, []);

  const restart = useCallback(() => {
    clearAllTimers();
    isWarningRef.current = false;
    setIsWarning(false);
    setRemainingMs(warningLeadMs);

    if (!enabled) return;

    const warningStartsAt = Math.max(idleTimeoutMs - warningLeadMs, 0);

    if (warningLeadMs > 0) {
      warningTimerRef.current = window.setTimeout(() => {
        isWarningRef.current = true;
        setIsWarning(true);
        const deadline = Date.now() + warningLeadMs;
        countdownRef.current = window.setInterval(() => {
          setRemainingMs(Math.max(deadline - Date.now(), 0));
        }, COUNTDOWN_TICK_MS);
      }, warningStartsAt);
    }

    idleTimerRef.current = window.setTimeout(() => {
      clearAllTimers();
      onIdleRef.current();
    }, idleTimeoutMs);
  }, [idleTimeoutMs, warningLeadMs, enabled, clearAllTimers]);

  const reset = useCallback(() => {
    restart();
    channelRef.current?.postMessage({ type: 'stay-signed-in' } satisfies IdleTimerMessage);
  }, [restart]);

  // Cross-tab: a local interaction resets here AND tells other tabs to reset too. Ignored once
  // the warning is showing — see isWarningRef above.
  const handleLocalActivity = useCallback(() => {
    if (isWarningRef.current) return;
    const now = Date.now();
    if (now - lastActivityAtRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityAtRef.current = now;
    restart();
    channelRef.current?.postMessage({ type: 'activity' } satisfies IdleTimerMessage);
  }, [restart]);

  useEffect(() => {
    if (!enabled || !channelName) return;

    const channel = new BroadcastChannel(channelName);
    channel.onmessage = (event: MessageEvent<IdleTimerMessage>) => {
      if (event.data?.type === 'stay-signed-in' || event.data?.type === 'activity') restart();
    };
    channelRef.current = channel;

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [enabled, channelName, restart]);

  useEffect(() => {
    restart();

    if (!enabled) return;

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleLocalActivity));

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleLocalActivity));
    };
  }, [restart, enabled, clearAllTimers, handleLocalActivity]);

  return { isWarning, remainingMs, reset };
}
